/**
 * DropZone Component
 *
 * Full-page drop zone overlay for file uploads.
 * Shows overlay when dragging files, uploads on drop.
 */

import { useState, useCallback, useEffect, useRef, type ReactNode, type DragEvent } from 'react';
import { Upload } from 'lucide-react';
import { useData } from '@/context/DataContext';
import { cn } from '@/utils/cn';

interface DropZoneProps {
  children: ReactNode;
}

export function DropZone({ children }: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const { uploadFile } = useData();

  // Track drag enter/leave to handle nested elements
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only show overlay for file drags
    if (e.dataTransfer.types.includes('Files')) {
      dragCounterRef.current += 1;
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(false);
      dragCounterRef.current = 0;

      const files = Array.from(e.dataTransfer.files);

      // Upload all dropped files
      for (const file of files) {
        try {
          await uploadFile(file);
        } catch (error) {
          console.error('Failed to upload file:', file.name, error);
        }
      }
    },
    [uploadFile]
  );

  // Reset drag state if component unmounts during drag
  useEffect(() => {
    return () => {
      setIsDragging(false);
      dragCounterRef.current = 0;
    };
  }, []);

  return (
    <div
      className="relative min-h-screen"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}

      {/* Drop overlay */}
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center pointer-events-none transition-opacity duration-200',
          isDragging ? 'opacity-100' : 'opacity-0'
        )}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-brand-600/20 backdrop-blur-sm" />

        {/* Drop indicator */}
        <div className="relative flex flex-col items-center gap-4 p-8 bg-white rounded-2xl shadow-2xl border-2 border-dashed border-brand-500">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-brand-100">
            <Upload className="w-8 h-8 text-brand-600" />
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">Drop files to upload</p>
            <p className="text-sm text-gray-500">Files will be saved to your inbox</p>
          </div>
        </div>
      </div>
    </div>
  );
}
